import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Flame, ArrowRight, Clock, BookOpen, Sparkles } from 'lucide-react';
import api from '@/services/api';
import { isAuthenticated, getUserData } from '@/utils/auth';
import { cn } from '@/lib/utils';
import { useSubjectChapter } from '@/context/SubjectChapterContext';

export default function ResumeLearningModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateSelection } = useSubjectChapter();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    // 1. Ensure user is authenticated for the student portal
    if (!isAuthenticated('student')) {
      return;
    }

    // Suppress if student still needs to complete parent's WhatsApp number
    const userData = getUserData('student');
    const isStudent = userData?.user_type === 'student';
    const hasPhone = Boolean(userData?.phone_no || userData?.parent_whatsapp_number);
    const hasGrace = sessionStorage.getItem('parent_phone_grace_used') === 'true';
    if (isStudent && !hasPhone && !hasGrace) {
      return;
    }

    // 2. Suppress while on a direct tutor link (?tutor= or /book/)
    const pathname = location.pathname || '';
    const hash = window.location.hash || '';
    const search = (location.search || '') + (window.location.search || '');

    const isTutorLink = pathname.startsWith('/book') ||
      hash.includes('/book/') ||
      search.includes('tutor=') ||
      hash.includes('tutor=');

    if (isTutorLink) {
      // Do not fetch or open modal while on tutor link, but do not set 'resume_modal_shown'
      // so it can still appear if the student navigates to Learn later in this session
      return;
    }

    // 3. Session check: only show once per browser tab session
    const hasShownThisSession = sessionStorage.getItem('resume_modal_shown') === '1';
    if (hasShownThisSession) {
      return;
    }

    let timeoutId = null;
    let isCancelled = false;

    const fetchResumeData = async () => {
      setLoading(true);
      try {
        const response = await api.get('users/resume-learning/');
        if (isCancelled) return;
        if (response.data && response.data.has_activity) {
          setData(response.data);
          // Mark as shown in sessionStorage so it doesn't trigger again in this session
          sessionStorage.setItem('resume_modal_shown', '1');

          // Open the modal after a short delay for smooth entry
          timeoutId = setTimeout(() => {
            if (!isCancelled) {
              setIsOpen(true);
            }
          }, 800);
        }
      } catch (err) {
        console.warn('Failed to load resume learning context:', err);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchResumeData();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location.pathname, location.search]);

  if (!isOpen || !data) return null;

  const { last_activity, next_step, deep_link_url, streak } = data;

  const handleContinue = () => {
    setIsOpen(false);

    const targetChapterId = next_step?.chapter_id || last_activity?.chapter_id;
    const targetSubjectId = next_step?.subject_id || last_activity?.subject_id;
    const targetSubjectName = next_step?.subject_name || last_activity?.subject_name || '';
    const targetChapterName = next_step?.chapter_name || last_activity?.chapter_name || '';

    // Set the selected subject and chapter so the learning workspace switches immediately
    if (targetChapterId) {
      updateSelection(
        String(targetSubjectId || ''),
        targetSubjectName,
        String(targetChapterId),
        targetChapterName
      );
    }

    // If next step is a quest, navigate to quest directly
    if (deep_link_url && deep_link_url.startsWith('/student/quests')) {
      navigate(deep_link_url);
      return;
    }

    // Check if student has a saved viewing position for this chapter
    let lastScreen = null;
    try {
      const raw = localStorage.getItem('budlee-v2-learn-last-screen');
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && String(parsed.chapterId) === String(targetChapterId)) {
        lastScreen = parsed;
      }
    } catch (e) { }

    // Build modern Budlee deep link resuming at the last unlocked/viewed card
    const params = new URLSearchParams();
    params.set('chapterId', String(targetChapterId));
    params.set('budlee', '1');

    if (lastScreen && Number.isFinite(Number(lastScreen.sectionIndex))) {
      params.set('budlee_section_index', String(lastScreen.sectionIndex));
      if (Number.isFinite(Number(lastScreen.stepIndex))) {
        params.set('budlee_step_index', String(lastScreen.stepIndex));
      }
      if (lastScreen.sectionId) {
        params.set('budlee_section_id', String(lastScreen.sectionId));
      }
    } else if (last_activity?.section_name) {
      params.set('section_title', last_activity.section_name);
    } else if (next_step?.section_name) {
      params.set('section_title', next_step.section_name);
    }

    navigate(`/student/learn?${params.toString()}`);
  };

  const handleDismiss = () => {
    setIsOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-all duration-300 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleDismiss();
      }}
    >
      <div className={cn(
        "fixed z-[10001] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] h-auto max-h-[90vh] bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] text-foreground shadow-2xl flex flex-col overflow-y-auto transition-all duration-300 rounded-3xl p-5 sm:p-6",
        "md:max-w-md md:w-full"
      )}>
        {/* Header - Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 rounded-full bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-all active:scale-95"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Top Welcome Section */}
        <div className="flex flex-col items-center text-center mt-1 mb-4">
          <div className="relative mb-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center shadow-lg shadow-emerald-500/20 animate-pulse">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            {streak > 0 && (
              <span className="absolute -bottom-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow-md border border-[hsl(var(--card-bg))] animate-bounce">
                <Flame className="w-2.5 h-2.5 fill-current" />
                {streak}
              </span>
            )}
          </div>

          <h2 className="text-xl font-black tracking-tight text-foreground">
            Welcome back!
          </h2>
        </div>

        {/* Activities Section */}
        <div className="space-y-3 mb-5">
          {/* Last Activity Card */}
          <div className="p-3 rounded-2xl bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] flex gap-3">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--muted))] flex items-center justify-center text-muted-foreground flex-shrink-0">
              <Clock className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                  Where you left off
                </span>
                {last_activity.time_label && (
                  <span className="text-[9px] font-semibold bg-[hsl(var(--muted))] text-foreground px-1.5 py-0.5 rounded-full">
                    {last_activity.time_label}
                  </span>
                )}
              </div>
              <p className="text-xs font-bold text-foreground line-clamp-2 mt-0.5">
                {last_activity.subject_name && `${last_activity.subject_name} • `}{last_activity.chapter_name}
              </p>
              <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                {last_activity.activity_label}
              </p>
            </div>
          </div>

          {/* Next Suggested Activity Card */}
          <div className="p-3 rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))]/10 to-[hsl(var(--green-bright))]/5 border border-[hsl(var(--primary))]/25 flex gap-3">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))]/15 flex items-center justify-center text-[hsl(var(--teal-vivid))] flex-shrink-0">
              <Sparkles className="w-4 h-4 animate-pulse" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold text-[hsl(var(--teal-vivid))] uppercase tracking-wider">
                  Recommended Next
                </span>
                {next_step.est_minutes && (
                  <span className="text-[9px] font-bold bg-[hsl(var(--primary))]/15 text-[hsl(var(--teal-vivid))] px-1.5 py-0.5 rounded-full">
                    ~{next_step.est_minutes} mins
                  </span>
                )}
              </div>
              <p className="text-xs font-bold text-foreground line-clamp-2 mt-0.5">
                {next_step.subject_name && `${next_step.subject_name} • `}{next_step.chapter_name}
              </p>
              <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                {next_step.label || 'Continue with next lesson'}
              </p>
            </div>
          </div>
        </div>

        {/* Buttons Footer */}
        <div className="flex flex-col sm:flex-row gap-2.5 w-full">
          <button
            onClick={handleDismiss}
            className="flex-1 order-2 sm:order-1 py-2.5 px-5 rounded-2xl border border-[hsl(var(--card-border))] bg-transparent hover:bg-[hsl(var(--sidebar-hover))] text-foreground font-bold text-sm transition-all active:scale-95"
          >
            Dismiss
          </button>
          <button
            onClick={handleContinue}
            className="flex-1 order-1 sm:order-2 py-2.5 px-5 rounded-2xl bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-95 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 active:scale-95"
          >
            <span>Continue</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
